import { ContentState, convertToRaw, Editor, EditorState } from 'draft-js';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// @mui/material components
import ReceiptIcon from '@mui/icons-material/Receipt';
import { makeStyles } from '@mui/styles';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardIcon from 'components/Card/CardIcon.js';
import Button from 'components/CustomButtons/Button.js';

import { ThemedSecondaryBtn } from '@themes/defaultThemeProps';

import { selectViewIsDirty, setViewIsDirty } from 'features/ux/uxSlice';

import 'draft-js/dist/Draft.css';

// style for this view
import styles from 'assets/jss/material-dashboard-pro-react/views/editTemplateStyle.js';
import Handlebars from 'handlebars/dist/cjs/handlebars';
import { useUpdateTemplateDictionaryMutation } from 'api/parameter.api';
import { useUpdateTemplateSetMutation } from 'api/parameter.api';

const useStyles = makeStyles(styles);
let pendingRedirectUrl = null;
let pendingRedirectPayload = {};

export default function TemplateEditor(props) {
  const classes = useStyles();
  const location = useLocation();
  const dispatch = useDispatch();
  const history = useHistory();
  const viewIsDirty = useSelector(selectViewIsDirty);
  const [updateTemplateDictionary] = useUpdateTemplateDictionaryMutation();
  const [updateTemplateSet] = useUpdateTemplateSetMutation();

  const parameterSet = {
    ...(props.parameterSet || location.state.parameterSet),
  };
  const tenantTemplate =
    props.tenantTemplate || location.state.tenantTemplate || false;
  const isNewTemplate = props.isNewTemplate || false;
  const onDialogClose = props.onDialogClose || null;
  const isDialogMode = props.isDialogMode || false;
  const template = props.template || location.state.template;
  const templateString = template.value || template.default;
  const strippedTemplate = templateString
    .replaceAll('{{#textline}}', '')
    .replaceAll('{{/textline}}', '');

  const [editorState, setEditorState] = React.useState(
    EditorState.createWithContent(ContentState.createFromText(strippedTemplate))
  );

  const editorChangeHandler = (data) => {
    dispatch(setViewIsDirty(true));
    setEditorState(data);
  };

  const [errorHB, setErrorHB] = useState(null);

  const validHandlebarFormat = (input) => {
    try {
      if (input.replaceAll('{{#textline}}{{/textline}}', '').trim() === '') {
        setErrorHB("Template can't be empty");
        return;
      }
      const precompiled = Handlebars.precompile(input);
      return precompiled.split('return').pop() !== input;
    } catch (e) {
      setErrorHB(e.message);
    }
  };

  const saveContent = async () => {
    const rawContent = convertToRaw(editorState.getCurrentContent());
    let updatedContent = '';
    rawContent.blocks.forEach((block) => {
      let controlHelper = false;
      if (
        block.text === '{{emphasis}}' ||
        block.text === '{{normal}}' ||
        block.text === '{{aligntextleft}}' ||
        block.text === '{{aligntextcenter}}' ||
        block.text === '{{cutfeed}}' ||
        block.text === '{{linefeed}}' ||
        block.text === '{{logo}}' ||
        block.text === '{{else}}'
      )
        controlHelper = true;
      const controlPrefix = block.text.substr(0, 3);
      if (
        !controlHelper &&
        controlPrefix !== '{{#' &&
        controlPrefix !== '{{/' &&
        controlPrefix !== '{{>'
      ) {
        updatedContent = updatedContent.concat('{{#textline}}');
        updatedContent = updatedContent.concat(block.text);
        updatedContent = updatedContent.concat('{{/textline}}\n');
      } else {
        updatedContent = updatedContent.concat(block.text);
        updatedContent = updatedContent.concat('\n');
      }
    });

    if (!validHandlebarFormat(updatedContent)) {
      return;
    }

    if (tenantTemplate === true) {
      // Update endpoint expects a parameterSet object
      const updatedSet = { ...parameterSet };
      updatedSet.parameters = updatedSet.parameters.map((item) => {
        const obj = {
          name: item.name,
          type: item.type,
          value: item.value,
          immutable:
            item.immutable === true ||
            item.immutable === 'true' ||
            item.immutable === 'True'
              ? true
              : false,
        };

        if (item.name === template.name) {
          obj.value = updatedContent;
          obj.changed = 'Yes';
        }

        return obj;
      });

      if (!isDialogMode) {
        if (!isNewTemplate) {
          await updateTemplateSet(updatedSet).unwrap();
        }
        dispatch(setViewIsDirty(false));
        parameterSet.parameters = parameterSet.parameters.map((item) => {
          const obj = { ...item }; // We can't mutate item directly
          if (item.name === template.name) {
            obj.value = updatedContent;
          }
          return obj;
        });
        pendingRedirectUrl = '/admin/templateset';
        pendingRedirectPayload = { parameterSet: parameterSet };
        dispatch(setViewIsDirty(false));
      } else {
        if (onDialogClose) {
          onDialogClose(updatedContent);
        }
      }
    } else {
      const updatedDictionaryEntry = {
        ...template,
        default: updatedContent,
      };

      await updateTemplateDictionary(updatedDictionaryEntry).unwrap();

      pendingRedirectUrl = '/admin/templatedictionary';
      dispatch(setViewIsDirty(false));
    }
  };

  useEffect(() => {
    if (pendingRedirectUrl && !isDialogMode) {
      // We always pass the parameterSet back to the redirect, its ok if its not updated in case of cancel
      // and both the templateSet and templateDictionary views can handle it
      props.history.push(
        pendingRedirectUrl,
        pendingRedirectPayload || { parameterSet: parameterSet }
      );
      pendingRedirectUrl = null;
      pendingRedirectPayload = {};
    }
  }, [viewIsDirty]);

  return (
    <>
      <Dialog
        open={errorHB}
        onClose={() => {
          setErrorHB(null);
        }}
        aria-labelledby='form-dialog-title'
        maxWidth={'md'}
        fullWidth
      >
        <DialogTitle id='form-dialog-title'>Error in template</DialogTitle>

        <DialogContent>
          <Grid container spacing={1}>
            <Grid item xs={7}>
              {errorHB}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <ThemedSecondaryBtn
            onClick={() => {
              setErrorHB(null);
            }}
            color='secondary'
          >
            Close
          </ThemedSecondaryBtn>
        </DialogActions>
      </Dialog>
      <Card style={{ boxShadow: isDialogMode ? 'none' : 'inherit' }}>
        <CardHeader color='rose' icon>
          <CardIcon color='rose'>
            <ReceiptIcon />
          </CardIcon>
          <h4 className={classes.cardIconTitle}>{template.name}</h4>
        </CardHeader>
        <CardBody>
          <Editor
            className={classes.editor}
            editorState={editorState}
            onChange={editorChangeHandler}
          />
          {tenantTemplate && (
            <Button
              color='rose'
              className={classes.registerButton}
              disabled={!viewIsDirty}
              onClick={saveContent}
            >
              Save Template
            </Button>
          )}
          <Button
            color='secondary'
            onClick={() => {
              if (onDialogClose && isDialogMode) {
                onDialogClose();
                return;
              }
              if (tenantTemplate) {
                history.push('/admin/templateset', {
                  parameterSet: parameterSet,
                });
              } else {
                history.goBack();
              }
            }}
            className={classes.registerButton}
          >
            {tenantTemplate ? 'Cancel' : 'Close'}
          </Button>
        </CardBody>
      </Card>
    </>
  );
}
